import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MapPinIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowLeftIcon,
    UserGroupIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tieredPackageService, TieredPackage } from '../services/tieredPackageService';
import { usePricing } from '../contexts/PricingContext';
import { pricingService } from '../services/pricingService';
import { currencyService } from '../services/currencyService';
import { PricingTier, FinalPrice } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/Common/SEO';
import StartPlanningButton from '../components/Common/StartPlanningButton';

const TieredPackageDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { actualTheme } = useTheme();
    const pricingContext = usePricing();
    const { tier: contextTier, nationalityType, displayCurrency, countryCode, pax } = pricingContext;
    const [pkg, setPkg] = useState<TieredPackage | null>(null);
    const [selectedTier, setSelectedTier] = useState<PricingTier>(contextTier);
    const [calculatedPrice, setCalculatedPrice] = useState<FinalPrice | null>(null);
    const [isLoadingPrice, setIsLoadingPrice] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        const loadPackage = async () => {
            if (!id) return;
            setIsLoading(true);
            try {
                const data = await tieredPackageService.getPackageById(id);
                setPkg(data);
            } catch (error) {
                console.error('Error loading package:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadPackage();
    }, [id]);

    useEffect(() => {
        if (!pkg) return;

        // Ensure selectedTier is valid for this package
        if (!pkg.tierVariants[selectedTier]) {
            // Find first available tier
            const availableTiers = [PricingTier.STANDARD, PricingTier.LUXURY, PricingTier.BUDGET];
            const firstAvailable = availableTiers.find(t => pkg.tierVariants[t]);
            if (firstAvailable) {
                setSelectedTier(firstAvailable);
            }
        }

        const calculatePrice = async () => {
            if (!pkg.tierVariants[selectedTier]) return;

            setIsLoadingPrice(true);
            try {
                const contextWithTier = { ...pricingContext, tier: selectedTier };
                const result = await pricingService.calculatePackagePrice(pkg, contextWithTier);
                setCalculatedPrice(result);
            } catch (error) {
                console.error('Error calculating price:', error);
            } finally {
                setIsLoadingPrice(false);
            }
        };

        calculatePrice();
    }, [pkg, selectedTier, nationalityType, displayCurrency, countryCode, pax]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800" />
            </div>
        );
    }

    if (!pkg) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Package Not Found</h2>
                    <button onClick={() => navigate('/packages')} className="text-primary-600 hover:underline">
                        Return to Packages
                    </button>
                </div>
            </div>
        );
    }

    const currentTierData = pkg?.tierVariants?.[selectedTier];

    const getTierColor = (tier: PricingTier) => {
        switch (tier) {
            case PricingTier.LUXURY:
                return 'from-amber-500 to-yellow-600';
            case PricingTier.STANDARD:
                return 'from-blue-500 to-indigo-600';
            case PricingTier.BUDGET:
                return 'from-green-500 to-emerald-600';
        }
    };

    const getTierLabel = (tier: PricingTier) => {
        switch (tier) {
            case PricingTier.LUXURY:
                return 'Luxury Experience';
            case PricingTier.STANDARD:
                return 'Standard Package';
            case PricingTier.BUDGET:
                return 'Budget Adventure';
        }
    };

    return (
        <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
            <SEO
                title={`${pkg.name} | Safari Packages`}
                description={pkg.overview || `Experience the best of Africa with our ${pkg.name} tiered safari package.`}
                image={pkg.gallery?.[0]}
                keywords={['safari package', pkg.name, ...pkg.destinations, 'africa tours']}
                structuredData={{
                    "@type": "Product",
                    name: pkg.name,
                    description: pkg.overview,
                    image: pkg.gallery?.[0],
                    brand: {
                        "@type": "Brand",
                        "name": "SightTrails Safaris"
                    }
                }}
            />
            {/* Header */}
            <div className="bg-primary-800 text-white py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
                    >
                        <ArrowLeftIcon className="h-5 w-5" />
                        Back
                    </button>
                    <h1 className="text-3xl md:text-4xl font-bold">{pkg.name}</h1>
                    <div className="flex items-center gap-4 mt-2 text-white/80">
                        <div className="flex items-center gap-1">
                            <ClockIcon className="h-5 w-5" />
                            <span>{pkg.durationDays} Days</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <MapPinIcon className="h-5 w-5" />
                            <span>{pkg.destinations.join(', ')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tier Selector */}
                <div className="mb-8">
                    <h2 className={`text-xl font-bold mb-4 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                        Choose Your Experience
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[PricingTier.LUXURY, PricingTier.STANDARD, PricingTier.BUDGET].map((tier) => {
                            const variant = pkg.tierVariants[tier];
                            if (!variant) return null; // Skip tiers that don't exist for this package

                            return (
                                <button
                                    key={tier}
                                    onClick={() => setSelectedTier(tier)}
                                    className={`p-6 rounded-xl border-2 transition-all ${selectedTier === tier
                                        ? `border-transparent bg-gradient-to-r ${getTierColor(tier)} text-white shadow-lg scale-105`
                                        : actualTheme === 'dark'
                                            ? 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600'
                                            : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-lg font-bold">{getTierLabel(tier)}</h3>
                                        {selectedTier === tier && <StarSolidIcon className="h-6 w-6" />}
                                    </div>
                                    <p className="text-sm opacity-90">{variant.guideClass}</p>
                                    <p className="text-sm opacity-90">{variant.vehicleType}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Gallery */}
                        <div className={`rounded-2xl overflow-hidden ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg`}>
                            <div className="relative h-96">
                                <img
                                    src={pkg.gallery[currentImageIndex]}
                                    alt={`${pkg.name} - Image ${currentImageIndex + 1}`}
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                />
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                                    {(pkg.gallery || []).map((_: string, idx: number) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentImageIndex(idx)}
                                            className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Overview */}
                        <div className={`p-6 rounded-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg`}>
                            <h2 className={`text-2xl font-bold mb-4 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                                Overview
                            </h2>
                            <p className={`${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>{pkg.overview}</p>
                        </div>

                        {/* Itinerary */}
                        <div className={`p-6 rounded-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg`}>
                            <h2 className={`text-2xl font-bold mb-6 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                                Day-by-Day Itinerary
                            </h2>
                            <div className="space-y-6">
                                {(currentTierData?.itinerary || []).map((day: any, idx: number) => (
                                    <div key={idx} className="flex gap-4">
                                        <div className={`flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r ${getTierColor(selectedTier)} flex items-center justify-center text-white font-bold`}>
                                            {day.day}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={`text-lg font-bold mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                                                {day.title}
                                            </h3>
                                            <p className={`text-sm mb-3 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                                                {day.description}
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {(day.activities || []).map((activity: string, actIdx: number) => (
                                                    <span
                                                        key={actIdx}
                                                        className={`px-3 py-1 rounded-full text-xs ${actualTheme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-neutral-100 text-neutral-700'
                                                            }`}
                                                    >
                                                        {activity}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inclusions & Exclusions */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className={`p-6 rounded-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg`}>
                                <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                                    Included
                                </h3>
                                <ul className="space-y-2">
                                    {currentTierData?.inclusions?.map((item: string, idx: number) => (
                                        <li key={idx} className={`flex items-start gap-2 text-sm ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>
                                            <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-500" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className={`p-6 rounded-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg`}>
                                <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                                    <XCircleIcon className="h-6 w-6 text-red-500" />
                                    Not Included
                                </h3>
                                <ul className="space-y-2">
                                    {currentTierData?.exclusions?.map((item: string, idx: number) => (
                                        <li key={idx} className={`flex items-start gap-2 text-sm ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>
                                            <XCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar - Pricing & Booking */}
                    <div className="lg:col-span-1">
                        <div className={`p-6 rounded-2xl ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'} shadow-lg sticky top-8`}>
                            <div className={`text-center mb-6 p-4 rounded-lg bg-gradient-to-r ${getTierColor(selectedTier)}`}>
                                <div className="text-white text-4xl font-bold mb-2">
                                    {isLoadingPrice ? (
                                        <span className="animate-pulse">...</span>
                                    ) : calculatedPrice ? (
                                        currencyService.formatCurrency(calculatedPrice.total, calculatedPrice.currency)
                                    ) : (
                                        'N/A'
                                    )}
                                </div>
                                <div className="text-white/90 text-sm">
                                    {pricingContext.nationalityType === 'LOCAL' ? 'Local Citizen Rate' : 'International Rate'}
                                </div>
                                <div className="text-white/80 text-xs mt-1">
                                    {pricingContext.pax.adults} Adults, {pricingContext.pax.children} Children
                                </div>
                            </div>

                            {calculatedPrice && (
                                <div className="mb-6">
                                    <h4 className={`text-sm font-semibold mb-3 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'}`}>
                                        Price Breakdown
                                    </h4>
                                    <div className="space-y-2">
                                        {(calculatedPrice.breakdown || []).map((item: any, idx: number) => (
                                            <div key={idx} className={`flex justify-between text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                                                <span>{item.item}</span>
                                                <span className="font-medium">
                                                    {currencyService.formatCurrency(item.total, calculatedPrice.currency)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Start Planning + Request Booking side-by-side */}
                            <div className="grid grid-cols-2 gap-3 mt-4">
                                <div className="w-full [&>div]:w-full [&>div>button#start-planning-btn]:w-full [&>div>button#start-planning-btn]:justify-center [&>div>button#start-planning-btn]:h-full [&>div>button#start-planning-btn]:px-2">
                                    <StartPlanningButton label="Plan" />
                                </div>
                                <button
                                    onClick={() => navigate(`/booking/${pkg.id}?tier=${selectedTier}`)}
                                    className={`w-full py-3.5 rounded-lg font-bold text-sm sm:text-base text-white bg-gradient-to-r ${getTierColor(selectedTier)} hover:shadow-xl transition-all`}
                                >
                                    Book Now
                                </button>
                            </div>

                            <p className={`text-xs text-center mt-4 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                                * Hotel allocation confirmed within 24 hours
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TieredPackageDetails;
